import React, { useState, useEffect, useRef, useCallback, useContext } from "react";
import ReactDOM from "react-dom";
import { useTranslation } from "react-i18next";
import Iconfont from "@components/iconfont/iconfont";
import { getkey } from "@util/utils";
import {
    fetchTeamLanguages,
    TEAM_LANG_CODES_UPDATED_EVENT,
} from "@util/teamLang";
import { SUPPORTED_LANGUAGES } from "@util/langConfig";
import { blurActiveAndWaitTranslations } from "@util/translate";
import privateContext from "@components/private_context";
import "./lang_switch.less";

/**
 * 支持的语言列表
 * key:   i18n 内部语言标识（与 initI18N.js 中 resources 的 key 对应）
 * label: 界面显示文本
 * value: 存入 sessionStorage('lang') 的值（同时作为 API 请求头的语言标识）
 */
// 由语言注册表派生：key=语言码，label=大写码（ZH/EN/JA），value=locale
const LANG_OPTIONS = SUPPORTED_LANGUAGES.map((l) => ({
    key: l.code,
    label: l.code.toUpperCase(),
    value: l.locale,
}));

const POPUP_GAP = 8;
const getEstimatedPopupHeight = (count) => {
    if (!count) return 0;
    return 8 + count * 40 + Math.max(count - 1, 0) * 2;
};

/**
 * 根据 sessionStorage 中存储的 lang 值，反查对应的语言 key
 * @param {string} value - sessionStorage 中的 lang 值，如 "zh-CN,zh"
 * @returns {string} 语言 key，如 "zh"
 */
const getLangKeyByValue = (value) => {
    const found = LANG_OPTIONS.find((item) => item.value === value);
    return found ? found.key : "zh";
};

/**
 * LangSwitch 语言切换组件
 *
 * 功能：
 * - 悬浮触发按钮：地球图标 + 当前语言标签 + 下拉箭头
 * - 点击后展开上方浮框，列出所有语言选项
 * - 切换语言无需刷新页面，通过 i18n.changeLanguage 响应式更新
 * - 同步更新 sessionStorage('lang')，确保后续 API 请求头使用新语言
 *
 * 通常配合 FloatGroup 使用，固定在页面右下角：
 * @example
 * <FloatGroup>
 *   <BackToTopBtn />
 *   <LangSwitch />
 * </FloatGroup>
 *
 * ─── Modal 偏移机制（lang-switch-offset 事件）────────────────────────────────
 * 当页面中的 Modal 打开时，组件会向左平移指定像素，避免被滚动条或 Modal 遮挡。
 * 各 Modal 组件在打开/关闭时通过向 window 派发自定义事件来控制偏移量：
 *
 * @example 打开 Modal 时（偏移 20px）
 * window.dispatchEvent(new CustomEvent("lang-switch-offset", { detail: { offset: 20 } }))
 *
 * @example 关闭 Modal 时（恢复原位）
 * window.dispatchEvent(new CustomEvent("lang-switch-offset", { detail: { offset: 0 } }))
 *
 * 不同 Modal 可以传入不同的 offset 值，组件会带 0.2s 过渡动画响应变化。
 * ────────────────────────────────────────────────────────────────────────────
 *
 * @param {object}   props
 * @param {function} [props.onChange]      - 语言切换后的外部回调，参数为语言 key（'zh' | 'en'）
 * @param {object}   [props.style]         - 传给地球图标的内联样式，黑白版场景下可用于修改图标颜色
 *                                           例：style={{ color: '#fff' }} 用于深色背景
 */
const LangSwitch = ({ onChange, style, teamId, useTeamDefault = true }) => {
    const { i18n } = useTranslation();
    const { setLang } = useContext(privateContext);

    const initLangValue = sessionStorage.getItem("lang") || "zh-CN,zh";
    const [currentKey, setCurrentKey] = useState(getLangKeyByValue(initLangValue));
    const [visible, setVisible] = useState(false);
    const [hadShow, setHadShow] = useState(false);
    const [popupPlacement, setPopupPlacement] = useState("top");
    // 弹窗实际展示的语言选项 = 团队已开通语言 ∩ 前端支持语言（默认全支持集，拉取后收窄）
    const [langOptions, setLangOptions] = useState(LANG_OPTIONS);

    useEffect(() => {
        const handleCodesUpdated = (event) => {
            const clientId = teamId || getkey("clientId");
            if (event.detail?.teamId && event.detail.teamId !== clientId) return;
            const nextOptions = (event.detail?.codes || [])
                .map((code) => LANG_OPTIONS.find((option) => option.key === code))
                .filter(Boolean);
            setLangOptions(nextOptions);
            setHadShow(nextOptions.some((option) => option.key !== "zh"));
        };
        window.addEventListener(TEAM_LANG_CODES_UPDATED_EVENT, handleCodesUpdated);
        return () =>
            window.removeEventListener(TEAM_LANG_CODES_UPDATED_EVENT, handleCodesUpdated);
    }, [teamId]);

    const triggerRef = useRef(null);
    const popupRef = useRef(null);
    const dragRef = useRef(null);
    const draggingRef = useRef(false);
    const dragStartRef = useRef({ x: 0, y: 0, posX: 0, posY: 0 });
    // 人工拖动位置（未受窗口压缩影响的基准）。窗口被压缩时按钮被顶入视窗内，
    // 需要记录相对该基准在各轴上的偏移；窗口反向还原时随视窗边界回到基准（偏移归零）。
    const anchorRef = useRef({ left: 0, top: 0 });
    const offsetRef = useRef({ x: 0, y: 0 });

    // 避免用户手动切换语言后又被默认语言覆盖
    const defaultApplied = useRef(false);

    // 跟踪 Modal 模式：当页面弹窗（CustomFieldModal / ShareModal 等）打开时，
    // LangSwitch 进入“Modal 模式”，切语言仅通知弹窗内部、不改全局 i18n。
    // checkLang 在 currentKey 变化时会被触发（含 Modal 模式下的切换），
    // 此时不能调用 applyGlobalLang 去修正 currentKey，否则会把按钮立即刷回全局语言。
    const modalOpenRef = useRef(false);

    const [pos, setPos] = useState({ right: 32, bottom: 132 });
    // posRef 始终镜像最新的 pos，供 clampToViewport 读取当前锚定/拖拽状态，避免闭包过期
    const posRef = useRef(pos);
    useEffect(() => {
        posRef.current = pos;
    }, [pos]);

    const syncContextLang = useCallback((nextLang) => {
        if (setLang) setLang(nextLang);
    }, [setLang]);

    const applyGlobalLang = useCallback((nextLang) => {
        const nextOpt = LANG_OPTIONS.find((o) => o.key === nextLang) || LANG_OPTIONS[0];
        setCurrentKey(nextOpt.key);
        sessionStorage.setItem("lang", nextOpt.value);
        syncContextLang(nextOpt.key);
        i18n.changeLanguage(nextOpt.key);
        onChange?.(nextOpt.key);
    }, [i18n, onChange, syncContextLang]);

    useEffect(() => {
        const handleDisabledCurrentLanguage = (event) => {
            const clientId = teamId || getkey("clientId");
            if (event.detail?.teamId && event.detail.teamId !== clientId) return;
            const enabledCodes = event.detail?.codes || [];
            const currentLang = LANG_OPTIONS.some((option) => option.key === i18n.language)
                ? i18n.language
                : getLangKeyByValue(sessionStorage.getItem("lang"));
            if (enabledCodes.includes(currentLang)) return;

            const defaultCode = enabledCodes.includes(event.detail?.defaultCode)
                ? event.detail.defaultCode
                : enabledCodes[0] || "zh";
            sessionStorage.removeItem("lang_user_picked");
            applyGlobalLang(defaultCode);
        };
        window.addEventListener(TEAM_LANG_CODES_UPDATED_EVENT, handleDisabledCurrentLanguage);
        return () =>
            window.removeEventListener(TEAM_LANG_CODES_UPDATED_EVENT, handleDisabledCurrentLanguage);
    }, [applyGlobalLang, i18n.language, teamId]);

    useEffect(() => {
        const saved = sessionStorage.getItem("lang-switch-pos");
        if (saved) {
            try {
                const parsed = JSON.parse(saved);
                setPos(parsed);
                // 保存的即为人工拖动位置，作为窗口压缩/还原的基准（anchor）
                anchorRef.current = { left: parsed.left ?? 0, top: parsed.top ?? 0 };
            } catch {
                // Ignore invalid saved drag position.
            }
        }
    }, []);

    useEffect(() => {
        const checkLang = async () => {
            // Modal 模式下 currentKey 代表弹窗内部语言，不是全局语言。
            // checkLang 的职责是维护「全局语言 ↔ 按钮」的一致性，不应在 Modal
            // 模式下把按钮刷回全局语言，否则用户刚切的弹窗语言会被立即冲掉。
            if (modalOpenRef.current) return;

            const clientId = teamId || getkey("clientId");
            if (!clientId) return;
            try {
                const { ok, hadLang, codes, defaultLanguage } = await fetchTeamLanguages(clientId);
                if (ok) {
                    setHadShow(hadLang);
                    setLangOptions(
                        codes
                            .map((code) => LANG_OPTIONS.find((option) => option.key === code))
                            .filter(Boolean)
                    );
                    const currentLang = LANG_OPTIONS.some((o) => o.key === i18n.language) ? i18n.language : currentKey;
                    if (!hadLang) {
                        if (currentLang !== "zh") {
                            applyGlobalLang("zh");
                        }
                        syncContextLang(false);
                        return;
                    }

                    // 查询团队默认语言（session 中已有用户手动选择的语言时跳过）
                    if (useTeamDefault && !defaultApplied.current && !sessionStorage.getItem('lang_user_picked')) {
                        defaultApplied.current = true;
                        const defaultCode = defaultLanguage?.languageCode;
                        const defaultOption = LANG_OPTIONS.find((option) => option.key === defaultCode);
                        if (defaultOption && currentKey !== defaultCode) {
                            applyGlobalLang(defaultCode);
                            return;
                        }
                    }
                    const nextLang = codes.includes(currentLang) ? currentLang : "zh";
                    if (nextLang !== currentLang || nextLang !== currentKey) {
                        applyGlobalLang(nextLang);
                        return;
                    }
                    syncContextLang(nextLang);
                }
            } catch {
                // Ignore language-pack lookup failures; the switch simply stays hidden.
            }
        };
        checkLang();
    }, [applyGlobalLang, currentKey, i18n, syncContextLang, teamId, useTeamDefault]);

    // 监听各 Modal 派发的偏移量事件，动态调整水平位置
    const [offsetX, setOffsetX] = useState(0);
    useEffect(() => {
        const handler = (e) => setOffsetX(e.detail?.offset ?? 0);
        window.addEventListener("lang-switch-offset", handler);
        return () => window.removeEventListener("lang-switch-offset", handler);
    }, []);

    const [hasMask, setHasMask] = useState(false);
    useEffect(() => {
        const checkMask = () => setHasMask(!!(document.querySelector(".ant-modal-mask") || document.querySelector(".bu-modal__overlay")));
        checkMask();
        const observer = new MutationObserver(checkMask);
        observer.observe(document.body, { childList: true, subtree: true });
        return () => observer.disconnect();
    }, []);

    // 监听置顶按钮的显隐：置顶按钮出现且本组件处于默认位置时，让位到距屏幕右边 100px
    const [scrollTopVisible, setScrollTopVisible] = useState(false);
    useEffect(() => {
        const handler = (e) => setScrollTopVisible(!!e.detail?.visible);
        window.addEventListener("lang-switch-scrolltop", handler);
        return () => window.removeEventListener("lang-switch-scrolltop", handler);
    }, []);

    // 语言可能被组件外部改掉（例如作品列表点「创建」时会强制 i18n.changeLanguage('zh')）。
    // currentKey 原本只在挂载时读一次 sessionStorage、之后只由本组件自己的 handleSelect
    // 和「弹窗关闭」事件更新，外部改语言时触发器上的语言标签不会跟着变，
    // 于是出现「界面已经是中文了，右下角按钮还显示 JA」。订阅 i18next 的 languageChanged 兜住。
    useEffect(() => {
        const handler = (lng) => {
            if (!lng) return;
            const nextLang = LANG_OPTIONS.some((o) => o.key === lng) ? lng : "zh";
            setCurrentKey(nextLang);
            if (hadShow) syncContextLang(nextLang);
        };
        i18n.on("languageChanged", handler);
        return () => i18n.off("languageChanged", handler);
    }, [hadShow, i18n, syncContextLang]);

    const [modalOpen, setModalOpen] = useState(false);
    useEffect(() => {
        const handler = (e) => {
            const isOpen = !!e.detail?.isOpen;
            setModalOpen(isOpen);
            modalOpenRef.current = isOpen;
            if (!isOpen) {
                const globalLang = sessionStorage.getItem("lang") || "zh-CN,zh";
                setCurrentKey(getLangKeyByValue(globalLang));
            }
        };
        window.addEventListener("lang-switch-modal-open", handler);
        return () => window.removeEventListener("lang-switch-modal-open", handler);
    }, []);

    // 当前语言配置项
    const currentOption = LANG_OPTIONS.find((item) => item.key === currentKey) || LANG_OPTIONS[0];

    const updatePopupPlacement = useCallback(() => {
        const trigger = triggerRef.current;
        if (!trigger) return;
        const triggerRect = trigger.getBoundingClientRect();
        const popupHeight = popupRef.current?.getBoundingClientRect().height || getEstimatedPopupHeight(langOptions.length);
        const spaceAbove = triggerRect.top;
        setPopupPlacement(spaceAbove < popupHeight + POPUP_GAP ? "bottom" : "top");
    }, [langOptions.length]);

    /**
     * 选择语言
     * - 同步更新 sessionStorage('lang')，确保后续 API 请求头使用新语言
     *
     * ─── Modal 独立语言机制（lang-switch-modal-open / lang-switch-modal-lang 事件）──────
     * 当 CustomFieldModal 等弹窗打开时，可通过事件让 LangSwitch 进入 Modal 模式：
     * 在此模式下，切换语言不会修改全局 i18n 和 sessionStorage，
     * 仅通过 lang-switch-modal-lang 事件通知 Modal 内部更新 localLang。
     *
     * @example 打开 Modal 时
     * window.dispatchEvent(new CustomEvent("lang-switch-modal-open", { detail: { isOpen: true } }))
     *
     * @example 关闭 Modal 时
     * window.dispatchEvent(new CustomEvent("lang-switch-modal-open", { detail: { isOpen: false } }))
     */
    const handleSelect = async (option) => {
        setCurrentKey(option.key);
        setVisible(false);
        if (modalOpen) {
            window.dispatchEvent(new CustomEvent("lang-switch-modal-lang", { detail: { key: option.key } }));
        }
        sessionStorage.setItem("lang", option.value);
        sessionStorage.setItem('lang_user_picked', '1');
        syncContextLang(option.key);
        // 切语言前先失焦当前编辑并等翻译完成：
        // 中文态未失焦就切语言时，新编辑的中文不会触发失焦翻译，目标语言槽会保留旧内容。
        await blurActiveAndWaitTranslations();
        i18n.changeLanguage(option.key);
        onChange?.(option.key);
    };

    const handleToggle = () => {
        if (draggingRef.current) return;
        setVisible((v) => {
            if (!v) updatePopupPlacement();
            return !v;
        });
    };

    useEffect(() => {
        if (!visible) return undefined;
        updatePopupPlacement();
        window.addEventListener("resize", updatePopupPlacement);
        return () => window.removeEventListener("resize", updatePopupPlacement);
    }, [updatePopupPlacement, visible]);

    useEffect(() => {
        const el = dragRef.current;
        if (!el) return;

        const startDrag = (clientX, clientY) => {
            draggingRef.current = false;
            const rect = el.getBoundingClientRect();
            dragStartRef.current = { x: clientX, y: clientY, posX: rect.left, posY: rect.top };
            return rect;
        };

        const moveDrag = (clientX, clientY, rect) => {
            const dx = clientX - dragStartRef.current.x;
            const dy = clientY - dragStartRef.current.y;
            if (!draggingRef.current && Math.abs(dx) < 4 && Math.abs(dy) < 4) return;
            draggingRef.current = true;
            setVisible(false);
            const newLeft = Math.max(0, Math.min(window.innerWidth - rect.width, dragStartRef.current.posX + dx));
            const newTop = Math.max(0, Math.min(window.innerHeight - rect.height, dragStartRef.current.posY + dy));
            setPos({ left: newLeft, top: newTop, right: undefined, bottom: undefined });
        };

        const endDrag = () => {
            const finalRect = el.getBoundingClientRect();
            // 拖拽结束位置即新的人工拖动基准，偏移清零；后续窗口压缩/还原从新基准计算
            anchorRef.current = { left: finalRect.left, top: finalRect.top };
            offsetRef.current = { x: 0, y: 0 };
            sessionStorage.setItem("lang-switch-pos", JSON.stringify({ left: finalRect.left, top: finalRect.top }));
            setTimeout(() => { draggingRef.current = false; }, 0);
        };

        const onMouseDown = (e) => {
            if (e.target.closest(".lang-switch-popup") || e.target.closest(".lang-switch-popup-item")) return;
            e.preventDefault();
            const rect = startDrag(e.clientX, e.clientY);

            const onMouseMove = (ev) => moveDrag(ev.clientX, ev.clientY, rect);
            const onMouseUp = () => {
                document.removeEventListener("mousemove", onMouseMove);
                document.removeEventListener("mouseup", onMouseUp);
                endDrag();
            };

            document.addEventListener("mousemove", onMouseMove);
            document.addEventListener("mouseup", onMouseUp);
        };

        const onTouchStart = (e) => {
            if (e.target.closest(".lang-switch-popup") || e.target.closest(".lang-switch-popup-item")) return;
            const touch = e.touches[0];
            const rect = startDrag(touch.clientX, touch.clientY);

            const onTouchMove = (ev) => {
                const t = ev.touches[0];
                moveDrag(t.clientX, t.clientY, rect);
                if (draggingRef.current) ev.preventDefault();
            };
            const onTouchEnd = () => {
                document.removeEventListener("touchmove", onTouchMove);
                document.removeEventListener("touchend", onTouchEnd);
                endDrag();
            };

            document.addEventListener("touchmove", onTouchMove, { passive: false });
            document.addEventListener("touchend", onTouchEnd);
        };

        el.addEventListener("mousedown", onMouseDown);
        el.addEventListener("touchstart", onTouchStart, { passive: true });
        return () => {
            el.removeEventListener("mousedown", onMouseDown);
            el.removeEventListener("touchstart", onTouchStart);
        };
    }, [hadShow]);

    // 拖拽定位（left/top）越界修正：以「人工拖动位置」为基准，把按钮约束在当前视窗内。
    // - 窗口被压缩：按钮被顶入视窗，记录各轴相对基准的偏移（offsetRef），
    //   但不覆盖 sessionStorage 中的人工拖动位置，保证窗口还原后能回到原位。
    // - 窗口反向还原：随视窗边界移回基准，直到各轴偏移归零。
    // 默认 right/bottom 锚定会跟随视窗边缘，天然不会越界，无需处理。
    const clampToViewport = useCallback(() => {
        const el = dragRef.current;
        if (!el) return;
        if (posRef.current.right !== undefined) return; // 默认锚定定位
        const rect = el.getBoundingClientRect();
        const anchor = anchorRef.current;
        const maxLeft = Math.max(0, window.innerWidth - rect.width);
        const maxTop = Math.max(0, window.innerHeight - rect.height);
        const left = Math.max(0, Math.min(maxLeft, anchor.left));
        const top = Math.max(0, Math.min(maxTop, anchor.top));
        // 记录各轴偏移距离（相对人工拖动位置）
        offsetRef.current = { x: anchor.left - left, y: anchor.top - top };
        if (left === rect.left && top === rect.top) return;
        setPos({ left, top, right: undefined, bottom: undefined });
    }, []);

    // 浏览器窗口尺寸变化时自动修正位置：压缩时顶入视窗并记录偏移，还原时直接回到人工拖动位置。
    useEffect(() => {
        const handleResize = () => clampToViewport();
        window.addEventListener("resize", handleResize);
        return () => window.removeEventListener("resize", handleResize);
    }, [clampToViewport]);

    // 页面刚打开（组件渲染到 body 后）时也做一次视窗越界判断：
    // 拖拽位置保存在 sessionStorage，若上次保存时的窗口比当前大，
    // 打开页面时按钮可能一开始就落在视窗之外，需要拉回可视区域内。
    useEffect(() => {
        clampToViewport();
    }, [clampToViewport, hadShow]);

    const popup = visible && (
        <>
            <div className="lang-switch-mask" onClick={() => setVisible(false)} />
            <div
                ref={popupRef}
                className={`lang-switch-popup lang-switch-popup-${popupPlacement}`}
            >
                {langOptions.map((option) => (
                    <div
                        key={option.key}
                        className={`lang-switch-popup-item ${option.key === currentKey ? "lang-switch-popup-item-active" : ""}`}
                        onClick={() => handleSelect(option)}
                    >
                        {option.label}
                    </div>
                ))}
            </div>
        </>
    );

    if (!hadShow) return null;

    // pos.right !== undefined 表示未被拖拽过，仍处于默认位置（right/bottom 锚定）
    const atDefaultAnchor = pos.right !== undefined;
    // 仅在默认位置时让位给置顶按钮，移动到距屏幕右边 100px
    const shiftForScrollTop = atDefaultAnchor && scrollTopVisible;
    const posStyle = atDefaultAnchor
        ? { right: shiftForScrollTop ? 100 : pos.right, bottom: pos.bottom }
        : { left: pos.left, top: pos.top };

    const transitions = [];
    if (offsetX) transitions.push("transform 0.2s");
    if (atDefaultAnchor) transitions.push("right 0.2s");

    return ReactDOM.createPortal(
        <div
            ref={dragRef}
            className="lang-switch"
            style={{
                position: "fixed",
                ...posStyle,
                ...(offsetX ? { transform: `translateX(-${offsetX}px)` } : {}),
                ...(transitions.length ? { transition: transitions.join(", ") } : {}),
                zIndex: 99999999,
                cursor: "grab",
            }}
        >
            {popup}
            <div
                ref={triggerRef}
                className={`lang-switch-trigger ${visible ? "lang-switch-trigger-open" : ""} ${hasMask ? "lang-switch-masked" : ""}`}
                onClick={handleToggle}
            >
                <Iconfont
                    type="icon-a-diqiuxi"
                    className="icon-a-diqiuxi"
                    style={style}
                />
                <span className="lang-switch-label">{currentOption.label}</span>
                <Iconfont
                    type="icon-a-danjiantouxiangxiaxi"
                    className={`lang-switch-arrow ${visible && popupPlacement === "top" ? "lang-switch-arrow-up" : ""}`}
                />
            </div>
        </div>,
        document.body
    );
};

export default LangSwitch;
